import { h } from 'preact';
import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';
import { isPowsterEmployee } from 'utils/roles';

import MediaAsset from 'components/editor/movies/movieMedia/mediaAsset';
import MovieMediaEditor from 'components/editor/movies/movieMedia/movieMedia';
import InheritIndicator from 'components/editor/settings/inheritIndicator';
import InlineSelect from 'components/inlineSelect/inlineSelect';

import s from 'components/editor/movies/movieMedia/movieMediaUniversal.sss';

export default class UniversalMovieMediaEditor extends MovieMediaEditor {
	s = s;

	constructor(props) {
		super(props);
		this.toggleHeroType = this.toggleHeroType.bind(this);
	}

	renderHeroVideo(opts = {}) {
		return super.renderHeroVideo({
			path: '/content/hero/desktop/video',
			inheritPath: '/base_movie/content/hero/desktop/video',
			label: 'Hero video (Landscape)',
			helpTip: 'Required for movie pages',
			...opts,
		});
	}

	renderHeroVideoMobile(opts = {}) {
		return (
			<MediaAsset
				{...this.props}
				path="/content/hero/mobile/video"
				inheritPath="/base_movie/content/hero/mobile/video"
				label="Hero video (Portrait)"
				assetData={{ type: 'Hero Video Mobile', recommendedDimensions: '540:800' }}
				helpTip="Required for movie pages"
				altPlaceholder={`${this.movieTitle} Hero Video`}
				key="hero-video-mobile"
				isVideo
				hideAlt
				{...opts}
			/>
		);
	}

	renderHeroVideoFirstFrame(opts = {}) {
		return (
			<MediaAsset
				{...this.props}
				path="/content/hero/desktop/fallbackImg"
				inheritPath="/base_movie/content/hero/desktop/fallbackImg"
				label="Hero video first frame (Landscape)"
				assetData={{ type: 'Hero Video Preload Desktop', aspectRatio: '16:9', recommendedDimensions: '1920:1080' }}
				helpTip="Shown while the video is loading"
				key="hero-video-fallback"
				hideAlt
				{...opts}
			/>
		);
	}

	renderHeroVideoFirstFrameMobile(opts = {}) {
		return (
			<MediaAsset
				{...this.props}
				path="/content/hero/mobile/fallbackImg"
				inheritPath="/base_movie/content/hero/mobile/fallbackImg"
				label="Hero video first frame (Portrait)"
				assetData={{ type: 'Hero Video Preload Mobile', recommendedDimensions: '540:800' }}
				helpTip="Shown while the video is loading"
				key="hero-video-fallback-mobile"
				hideAlt
				{...opts}
			/>
		);
	}

	renderHeroImage(opts = {}) {
		return super.renderHeroImage({
			path: '/content/hero/desktop/still',
			inheritPath: '/base_movie/content/hero/desktop/still',
			label: 'Hero still (Landscape)',
			...opts,
		});
	}

	renderHeroImageMobile(opts = {}) {
		return (
			<MediaAsset
				{...this.props}
				path="/content/hero/mobile/still"
				inheritPath="/base_movie/content/hero/mobile/still"
				label="Hero still (Portrait)"
				assetData={{ type: 'Hero Still Mobile', recommendedDimensions: '540:800' }}
				key="hero-still-mobile"
				hideAlt
				{...opts}
			/>
		);
	}

	renderFavicon(opts = {}) {
		return super.renderFavicon({
			inheritPath: '/base_movie/content/metaData/favicon',
			...opts,
		});
	}

	renderGallery(opts = {}) {
		return super.renderGallery({
			inheritPath: '/base_movie/content/pages/gallery/images',
			...opts,
		});
	}

	renderFooterLogos(opts = {}) {
		return super.renderFooterLogos({
			inheritPath: '/base_movie/content/footer/logos',
			...opts,
		});
	}

	renderMiscAssets(opts = {}) {
		return super.renderMiscAssets({
			inheritPath: '/base_movie/content/miscAssets',
			...opts,
		});
	}

	toggleHeroType(e) {
		jsonPointer.setDeep(Store.get().data, `${this.props.dataRoot}/content/hero/type`, e.target.value);
	}

	renderSectionHero() {
		const { dataRoot } = this.props;
		const heroTypePath = `/content/hero/type`;
		const inheritedHeroType = jsonPointer.get(Store.get().data, `${dataRoot}/base_movie${heroTypePath}`);
		const heroType = jsonPointer.get(Store.get().data, `${dataRoot}${heroTypePath}`) || inheritedHeroType;

		return (
			<div>
				<div class={s.heroTypeSelector}>
					<InlineSelect name="hero-type" value={heroType} defaultValue="image" onChange={this.toggleHeroType}>
						<option value="video">Video</option>
						<option value="image">Image</option>
					</InlineSelect>
					{!!inheritedHeroType && <InheritIndicator path={`${dataRoot}${heroTypePath}`} align="middle" />}
				</div>
				<div class={this.s.assetsRow}>
					{heroType === 'video' && this.renderHeroVideo()}
					{heroType === 'video' && this.renderHeroVideoMobile()}
					{heroType === 'video' && this.renderHeroVideoFirstFrame()}
					{heroType === 'video' && this.renderHeroVideoFirstFrameMobile()}
					{this.renderHeroImage()}
					{heroType === 'image' && this.renderHeroImageMobile()}
				</div>
			</div>
		);
	}

	renderInheritIndicator({ path, ...props } = {}) {
		const { dataRoot } = this.props;

		const canInherit = !!jsonPointer.get(Store.get().data, `${dataRoot}/base_movie${path}`);
		if (!canInherit) return;

		return (
			<InheritIndicator
				path={`${dataRoot}${path}`}
				align="middle"
				group
				{...props}
			/>
		);
	}

	getSections() {
		const showMiscAssets = isPowsterEmployee() || Object.keys(this.movie?.miscAssets || {}).length > 0;

		return [
			{ label: 'Key art', content: [this.renderOnesheet(), this.renderHeaderImage(), this.renderTitleTreatment()] },
			{ label: 'Supporting assets', content: [this.renderFavicon(), this.renderShareImage()] },
			{ label: 'Hero', note: 'Select either a video or image background for the hero section on the landing page', content: this.renderSectionHero() },
			{ label: 'YouTube trailers', note: 'Drag to reorder', content: this.renderTrailers() },
			{ label: 'Gallery', note: 'Drag to reorder', actions: this.renderInheritIndicator({ path: '/content/pages/gallery/images' }), content: this.renderGallery() },
			{ label: 'Footer logos', note: 'Drag to reorder', actions: this.renderInheritIndicator({ path: '/content/footer/logos' }), content: this.renderFooterLogos() },
			showMiscAssets && { label: 'Miscellaneous assets', note: 'Assets used for custom implementations', content: this.renderMiscAssets() },
		].filter(Boolean);
	}

	getSettings() {
		return [
			...super.getSettings(),
			this.renderHeroVideoMobile(),
			this.renderHeroVideoFirstFrame(),
			this.renderHeroVideoFirstFrameMobile(),
			this.renderHeroImageMobile(),
		];
	}
}